import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { useState } from 'react'
import { z } from 'zod'
import { useMutation } from 'urql'
import Button from '../Button'
import FormCombo from '../form/FormCombo'
import FormHiddenInput from '../form/FormHiddenInput'
import FormInput from '../form/FormInput'
import { SmartForm } from '../form/SmartForm'
import type { ScorecardJson } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'
import { useScorecardStore } from 'app/stores/StoreContext'

const CREATE_SCORECARD = graphql(`
  mutation CreateScorecard($input: CreateScorecardInput!) {
    createScorecard(input: $input) {
      id
    }
  }
`)

const UPDATE_SCORECARD = graphql(`
  mutation UpdateScorecard($id: ID!, $input: UpdateScorecardInput!) {
    updateScorecard(id: $id, input: $input) {
      id
    }
  }
`)

const createSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, { message: `A name is required` }),
  groupBy: z.enum([`Location`, `Staff`]),
  markAsDefault: z.string().transform((value) => value === `true`),
})

const editSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, { message: `A name is required` }),
  markAsDefault: z.string().transform((value) => value === `true`),
})

export const createValidator = withZod(createSchema) as any

export const editValidator = withZod(editSchema) as any

export function CreateNewScorecardModal({
  editingScorecard,
  onScorecardCreatedOrUpdated,
  onScorecardDeleted,
}: {
  editingScorecard?: ScorecardJson
  onScorecardCreatedOrUpdated: (id: string) => void
  onScorecardDeleted?: () => void
}) {
  const [selectedGrouping, setSelectedGrouping] = useState<OptionType>({
    id: `location`,
    name: `Locations`,
    value: `Location`,
  })
  const [markAsDefault, setMarkAsDefault] = useState(editingScorecard?.default ?? false)
  const [deleting, setDeleting] = useState(false)
  const [createResult, executeCreate] = useMutation(CREATE_SCORECARD)
  const [updateResult, executeUpdate] = useMutation(UPDATE_SCORECARD)
  const saving = createResult.fetching || updateResult.fetching || deleting
  const scorecardStore = useScorecardStore()

  const handleSubmit = async (data: any) => {
    if (editingScorecard) {
      const { data: result, error } = await executeUpdate({
        id: editingScorecard.id!,
        input: { name: data.name, default: markAsDefault },
      })
      if (error) {
        notifyError(`Failed to update scorecard: ${error.message}`)
        return
      }
      notifySuccess(`Scorecard updated successfully`)
      closeModal()
      onScorecardCreatedOrUpdated(result!.updateScorecard.id)
    } else {
      const { data: result, error } = await executeCreate({
        input: { name: data.name, grouping: data.groupBy, default: markAsDefault },
      })
      if (error) {
        notifyError(`Failed to create scorecard: ${error.message}`)
        return
      }
      notifySuccess(`Scorecard created successfully`)
      closeModal()
      onScorecardCreatedOrUpdated(result!.createScorecard.id)
    }
  }

  const onDeleteClick = async () => {
    if (editingScorecard) {
      setDeleting(true)
      try {
        const result = await scorecardStore.remove(editingScorecard.id!)
        if (result.error) {
          notifyError(`Failed to delete scorecard: ${result.error.message}`)
        } else {
          notifySuccess(`Scorecard deleted successfully`)
          closeModal()
          onScorecardDeleted?.()
        }
      } finally {
        setDeleting(false)
      }
    }
  }

  return (
    <div className="flex w-[750px] flex-col items-center justify-center">
      <div className="mb-6 w-full px-4">
        <SmartForm
          id="scorecard-form"
          onCancel={() => closeModal()}
          validator={editingScorecard ? editValidator : createValidator}
          defaultValues={
            editingScorecard
              ? { id: editingScorecard.id, name: editingScorecard.name, markAsDefault: editingScorecard.default }
              : undefined
          }
          hiddenInputs={editingScorecard ? [{ name: `id`, value: editingScorecard.id }] : undefined}
          hideButtons={true}
          preventSubmit
          onSubmit={handleSubmit}
        >
          <FormHiddenInput name="markAsDefault" value={`${markAsDefault}`} />
          <FormInput name="name" label="Scorecard Name" labelClassName="text-body-sm text-strong" cols={6} />
          <FormCombo
            cols={6}
            name="groupBy"
            label="Grouped by"
            value={selectedGrouping}
            options={[
              { id: `location`, name: `Locations`, value: `Location` },
              { id: `staff`, name: `Staff`, value: `Staff` },
            ]}
            labelClassName="text-body-sm text-strong"
            onChange={(option) => setSelectedGrouping(option)}
            disabled={editingScorecard ? true : undefined}
            showSearchInDropdown={true}
            newLineError={false}
            showErrorIcon
          />
        </SmartForm>
      </div>
      <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
        <div className="flex justify-between align-middle">
          <label htmlFor="mark-as-default" className="flex cursor-pointer items-center space-x-2 py-1.5 capitalize">
            <input
              id="mark-as-default"
              type="checkbox"
              className="cursor-pointer rounded text-ui-xs text-strong"
              checked={markAsDefault}
              onChange={(evt) => {
                setMarkAsDefault(evt.target.checked)
              }}
            />
            <span className="text-sm font-medium leading-none text-subtle">Mark as default</span>
          </label>
          <div className="flex justify-end gap-x-4 align-middle">
            {!!editingScorecard && <Button label="Delete" mode="danger" size="lg" onClick={() => onDeleteClick()} />}
            <Button label="Cancel" mode="white" size="lg" onClick={() => closeModal()} />
            <Button type="submit" form="scorecard-form" label="Save" mode="primary" size="lg" />
          </div>
        </div>
      </div>
    </div>
  )
}
